import {
  Wind,
  CloudRain,
  Sparkles,
  Users,
  HeartCrack,
  BatteryLow,
  Sunrise,
  Brain,
  HandHeart,
} from "lucide-react";
import { TESTIDS, WHATSAPP_URL } from "@/lib/site";
import { trackWhatsAppClick } from "@/lib/analytics";
import { useReveal } from "@/lib/useReveal";

const TREATMENTS = [
  {
    key: "ansiedad",
    title: "Ansiedad",
    icon: <Wind size={20} />,
    text: "Acompañar la sensación constante de alerta. Encontrar pausas, comprender lo que el cuerpo intenta decir y volver a habitarte con calma.",
  },
  {
    key: "depresion",
    title: "Depresión",
    icon: <CloudRain size={20} />,
    text: "Un espacio sin prisa para nombrar lo que pesa. Volver a conectar, poco a poco, con el sentido, el deseo y la propia voz.",
  },
  {
    key: "autoestima",
    title: "Autoestima",
    icon: <Sparkles size={20} />,
    text: "Mirarte con menos exigencia y más ternura. Reconstruir una relación honesta y amable con quien eres en este momento de tu vida.",
  },
  {
    key: "relaciones",
    title: "Relaciones",
    icon: <Users size={20} />,
    text: "Explorar tus vínculos —pareja, familia, amistades— para construir formas de relación más sanas, claras y respetuosas para todos.",
  },
  {
    key: "crisis",
    title: "Crisis personales",
    icon: <HeartCrack size={20} />,
    text: "Cuando algo se rompe o se transforma. Un acompañamiento sostenido para atravesar el momento sin tener que hacerlo en soledad.",
  },
  {
    key: "agotamiento",
    title: "Agotamiento emocional",
    icon: <BatteryLow size={20} />,
    text: "Reconocer cuándo el cuerpo y la mente piden detenerse. Recuperar energía, límites sanos y un ritmo de vida más sostenible.",
  },
  {
    key: "transiciones",
    title: "Transiciones de vida",
    icon: <Sunrise size={20} />,
    text: "Mudanzas, maternidad, duelos, cambios de etapa. Un espacio para atravesar lo nuevo con escucha, claridad y sostén emocional.",
  },
  {
    key: "neurodivergencia",
    title: "Neurodivergencia",
    icon: <Brain size={20} />,
    text: "Una mirada respetuosa hacia mentes que perciben distinto. Un proceso adaptado, sin etiquetas, centrado en tu forma única de habitar el mundo.",
  },
  {
    key: "acompanamiento",
    title: "Acompañamiento emocional",
    icon: <HandHeart size={20} />,
    text: "Un proceso continuo de escucha activa y presencia. Cuando lo que necesitas, antes que todo, es alguien que sepa estar.",
  },
];

const TONES = [
  "bg-dusty-rose/20 border-dusty-rose/40 text-accent-magenta",
  "bg-sage/20 border-sage/40 text-[#3F5340]",
  "bg-lavender/25 border-lavender/50 text-accent-magenta",
  "bg-warm-beige/70 border-warm-beige text-terracotta",
];

export default function Treatments() {
  const r = useReveal();

  return (
    <section
      id="tratamientos"
      data-testid={TESTIDS.treatments.section}
      className="relative py-24 md:py-32 bg-warm-beige/40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className={`${r.className} max-w-2xl mb-14`}>
          <p className="text-[0.78rem] tracking-[0.22em] uppercase text-accent-magenta mb-4">
            Tratamientos
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-[3.4rem] text-editorial-dark leading-[1.05]">
            Acompañamientos
            <span className="italic text-terracotta"> hechos para ti</span>.
          </h2>
          <p className="mt-5 text-editorial-gray text-[1.02rem] leading-relaxed">
            Cada proceso es único. Estos son algunos de los temas que se trabajan en
            consulta, siempre desde la escucha activa, la sensibilidad y la mirada
            humanista del bienestar emocional.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {TREATMENTS.map((t, i) => (
            <article
              key={t.key}
              data-testid={TESTIDS.treatments.card(t.key)}
              className="group relative overflow-hidden rounded-[1.5rem] bg-warm-white border border-dusty-rose/25 p-7 hover:-translate-y-1 transition-all duration-500 shadow-sm hover:shadow-md hover:shadow-editorial-dark/5"
            >
              <span
                className={`inline-flex w-12 h-12 items-center justify-center rounded-full border ${
                  TONES[i % TONES.length]
                }`}
              >
                {t.icon}
              </span>
              <h3 className="mt-5 font-serif text-[1.55rem] text-editorial-dark leading-tight">
                {t.title}
              </h3>
              <p className="mt-2.5 text-[0.95rem] text-editorial-gray leading-relaxed">
                {t.text}
              </p>
              <span className="absolute inset-x-7 bottom-0 h-px bg-gradient-to-r from-transparent via-dusty-rose/30 to-transparent group-hover:via-accent-magenta/40 transition-colors" />
            </article>
          ))}
        </div>

        <div className="mt-14 text-center">
          <p className="text-editorial-gray italic max-w-xl mx-auto">
            ¿No encuentras tu motivo aquí? Cada historia es distinta. Escríbeme y
            encontraremos juntas el espacio adecuado para ti.
          </p>
          <a
            data-testid={TESTIDS.treatments.cta}
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWhatsAppClick("treatments_cta")}
            className="mt-7 inline-flex items-center gap-2.5 rounded-full bg-accent-magenta text-warm-white px-7 py-3.5 text-[0.95rem] hover:-translate-y-0.5 transition-all shadow-md shadow-accent-magenta/20"
          >
            Agendar consulta por WhatsApp
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}
